import React, { memo, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { RiskMinor } from '@shopify/polaris-icons'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikSwitch } from 'src/components/shared/form/formik/FormikSwitch'
import {
  getDocusignPartnerTemplates,
  getDocusignTemplateSelectOptions,
  mergeDocusignTemplateSelectOptions,
  resolveDocusignTemplateSelectValue,
} from 'src/selectors/collectStepReference'
import { getCustomer } from 'src/selectors/customer'
import { isWireTransferRequired } from 'src/utils/gigsafeConfig'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { CustomerSettingsFormValues } from './CustomerSettingsForm.interface'

interface Props {
  isReadOnly: boolean
}

const FeaturesAndTransfersCardComponent = ({ isReadOnly }: Props) => {
  const { values, setFieldValue } = useFormikContext<CustomerSettingsFormValues>()

  const customer = useSelector(getCustomer)
  const docusignTemplates = useSelector(getDocusignPartnerTemplates)
  const docusignOptions = useSelector(getDocusignTemplateSelectOptions)

  // Keeps a stored template that is missing from the fetched list visible as a labelled option
  // instead of silently blanking the select.
  const templateOptions = useMemo(() => {
    return mergeDocusignTemplateSelectOptions(
      docusignOptions,
      docusignTemplates,
      values.masterContractorDocusignTemplateId,
    )
  }, [docusignOptions, docusignTemplates, values.masterContractorDocusignTemplateId])

  // Some rows store DocuSign's own template UUID instead of the partner-template row id. The
  // templates list loads asynchronously, so on first render it's empty and nothing can be
  // resolved yet; once it lands, swap a legacy UUID for the canonical row id so the select
  // shows the template's name and a save writes an id `partnerContractTemplateService.getById()`
  // can resolve. Comparing `resolved` to the current value before writing makes this a no-op
  // once the field already holds the canonical id, so it neither loops nor overwrites a template
  // the admin has since picked themselves.
  useEffect(() => {
    if (docusignTemplates.length === 0) {
      return
    }

    const resolved = resolveDocusignTemplateSelectValue(
      docusignTemplates,
      values.masterContractorDocusignTemplateId,
    )

    if (resolved === values.masterContractorDocusignTemplateId) {
      return
    }

    setFieldValue('masterContractorDocusignTemplateId', resolved, false)
  }, [docusignTemplates, values.masterContractorDocusignTemplateId, setFieldValue])

  const showWireTransferWarning = useMemo(() => {
    return isWireTransferRequired(customer?.products, {
      loanTransferEnabled: values.loanTransferEnabled,
      wireTransferEnabled: values.wireTransferEnabled,
    })
  }, [customer?.products, values.loanTransferEnabled, values.wireTransferEnabled])

  return (
    <SectionCard
      title="Features & transfers"
      subtitle="Turn transfer types on or off for this customer."
    >
      <FormikSwitch<CustomerSettingsFormValues>
        name="loanTransferEnabled"
        label="Loan Transfers"
        description="Requires Stitch payment settings and an Oatfi business payment method. Saving without both fails with 'Loan is not available to enable'."
        disabled={isReadOnly}
      />

      <FormikSwitch<CustomerSettingsFormValues>
        name="wireTransferEnabled"
        label="Wire Transfers"
        disabled={isReadOnly}
      />

      {showWireTransferWarning ? (
        <div css={tw`flex items-start gap-2 rounded-lg bg-warning-100 p-3`}>
          <RiskMinor width={20} css={tw`shrink-0 fill-warning-500`} />
          <Typography variant="body2" css={tw`text-shades-700`}>
            This customer uses Payments, so a transfer route is required. Turn on Wire Transfers, or
            enable Loan Transfers instead.
          </Typography>
        </div>
      ) : null}

      <FormikSelect<CustomerSettingsFormValues>
        name="masterContractorDocusignTemplateId"
        label="Master Contractor DocuSign template"
        placeholder="Select a template"
        options={templateOptions}
        disabled={isReadOnly}
      />
    </SectionCard>
  )
}

export const FeaturesAndTransfersCard = memo(FeaturesAndTransfersCardComponent)
